import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SafeImg from '@/components/shared/SafeImg';
import LockedMediaMask from '@/components/shared/LockedMediaMask';
import UnlockButton from '@/components/shared/UnlockButton';
import thumbnail_video_blur from '@/assets/imgs/placeholders/thumbnail_video_blur.jpg';

const MediaThumbnail = ({ media }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);

  const isVideo = media.type === 1;
  const isLocked = media.post_info.post_is_locked;

  // 鎖定時圖片用後端給的 blur 版 thumbnail_url，影片一律用靜態佔位圖
  // （後端不保證給鎖定影片的縮圖）——與 PostImages 鎖定分支同一套規則
  const src = isLocked
    ? (isVideo ? thumbnail_video_blur : media.thumbnail_url)
    : (isVideo ? (media.thumbnail_url || thumbnail_video_blur) : media.url);

  const renderVideoOverlay = () => {
    const status = media.stream_status;
    if (status === 'error') {
      return (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-75" style={{ pointerEvents: 'none' }}>
          <FontAwesomeIcon icon={['fas', 'circle-exclamation']} className="text-danger fs-5 mb-1" />
          <span className="text-white fs-12">{t('video_process_failed')}</span>
        </div>
      );
    }
    if (status !== 'ready') {
      return (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-75" style={{ pointerEvents: 'none' }}>
          <div className="spinner-border text-white mb-1" style={{ width: '1.25rem', height: '1.25rem' }} role="status" />
          <span className="text-white fs-12">{t('video_processing')}</span>
        </div>
      );
    }
    if (!isLoaded) {
      return (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50" style={{ pointerEvents: 'none' }}>
          <div className="spinner-border text-white" style={{ width: '1.25rem', height: '1.25rem' }} role="status" />
        </div>
      );
    }
    return (
      <div className="position-absolute top-50 start-50 translate-middle" style={{ pointerEvents: 'none' }}>
        <div className="bg-dark bg-opacity-75 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
          <FontAwesomeIcon icon={['fas', 'play']} className="text-white fs-6" />
        </div>
      </div>
    );
  };

  return (
    <div
      className="media-grid-item position-relative pointer"
      onClick={() => {
        window.scrollTo(0, 0);
        navigate(`/post/${media.post_info.post_uuid}`);
      }}
    >
      <SafeImg
        src={src}
        alt={isLocked ? 'Locked Content' : (media.caption || '')}
        onLoad={() => setIsLoaded(true)}
        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
      />

      {/* 鎖定：模糊圖上疊遮罩＋類型 icon＋解鎖按鈕（純樣式，不掛 handler，點擊由整格導向該貼文）。
          與貼文一致，鎖定時不顯示影片播放鍵／處理中狀態 */}
      {isLocked ? (
        <>
          <LockedMediaMask mediaType={media.type} />
          {/* w-100 + px-2 讓長文案（日文 + 3 位數價格）能在窄格子裡換行，不被 overflow 切斷 */}
          <div className="locked-media-action position-absolute top-50 start-50 translate-middle w-100 px-2 text-center">
            <UnlockButton
              lockType={media.post_info.post_lock_type}
              unlockPrice={media.post_info.post_unlock_price}
              size="sm"
            />
          </div>
        </>
      ) : (
        isVideo && renderVideoOverlay()
      )}
    </div>
  );
};

export default MediaThumbnail;
